import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import axios from 'axios';
import { Volleyball, Eye, EyeSlash, CheckCircle, ArrowLeft } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError(t('forgot.minLengthError'));
      return;
    }
    if (password !== confirm) {
      setError(t('forgot.mismatchError'));
      return;
    }
    setStatus('loading');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setStatus('done');
      setTimeout(() => navigate('/login'), 3500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail, t('forgot.resetError')));
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-[#73706A] hover:text-[#1A1A1A] mb-6 text-sm"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft size={16} /> {t('forgot.backToLogin')}
        </Link>

        <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#E85D04] rounded-xl flex items-center justify-center">
              <Volleyball size={28} className="text-white" weight="fill" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('forgot.resetTitle')}</h1>
              <p className="text-sm text-[#73706A]">Tia Karol Sports</p>
            </div>
          </div>

          {status === 'done' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center" data-testid="reset-password-success">
              <CheckCircle size={48} className="text-[#2D6A4F] mx-auto mb-3" weight="fill" />
              <h2 className="font-semibold text-[#2D6A4F] text-lg">{t('forgot.resetSuccess')}</h2>
              <p className="text-sm text-[#1A1A1A] mt-2">{t('forgot.resetSuccessBody')}</p>
              <p className="text-xs text-[#73706A] mt-2">{t('forgot.redirecting')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="reset-password-form">
              <p className="text-sm text-[#1A1A1A] mb-5">{t('forgot.resetSubtitle')}</p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[#9B2226] text-sm" data-testid="reset-password-error">
                  {error}
                </div>
              )}

              <label className="block mb-4">
                <span className="text-sm font-medium text-[#1A1A1A]">{t('forgot.newPassword')}</span>
                <div className="relative mt-1">
                  <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pr-12"
                    placeholder={t('forgot.minLength')}
                    required
                    minLength={6}
                    data-testid="reset-password-new-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#73706A] hover:text-[#1A1A1A]"
                    data-testid="reset-password-toggle-show"
                  >
                    {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </label>

              <label className="block">
                <span className="text-sm font-medium text-[#1A1A1A]">{t('forgot.confirmNewPassword')}</span>
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-field mt-1"
                  placeholder={t('forgot.repeatPassword')}
                  required
                  minLength={6}
                  data-testid="reset-password-confirm-input"
                />
              </label>

              <button
                type="submit"
                disabled={status === 'loading' || !password || !confirm}
                className="mt-6 w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-password-submit-btn"
              >
                {status === 'loading' ? t('forgot.resetting') : t('forgot.resetButton')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
